import { lazy, Suspense, useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AdminLayout } from '@/pages/AdminLayout'
import { AdminDashboard } from '@/pages/AdminDashboard'
import { UserManagement } from '@/pages/UserManagement'
import { OrganizationManagement } from '@/pages/OrganizationManagement'
import { OrganizationDetailPage } from '@/pages/OrganizationDetailPage'
import { SubscriptionManagement } from '@/pages/SubscriptionManagement'
import { OrgSubscriptionManagement } from '@/pages/OrgSubscriptionManagement'
import { ProductManagement } from '@/pages/ProductManagement'
import { ContentManagement } from '@/pages/ContentManagement'
import { ConfigurationManagement } from '@/pages/ConfigurationManagement'
import { NotesPage } from '@/pages/NotesPage'
import { SharedMediaLibraryPage } from '@/pages/SharedMediaLibraryPage'
import { LoginPage } from '@/pages/LoginPage'
import { AnnouncementListPage, AnnouncementEditPage, AnnouncementDetailPage } from '@/pages/announcements'
import { SupportInquiryPage } from '@/pages/support'
import { DataStatsPage } from '@/pages/DataStatsPage'
import { LearningStatusPage } from '@/pages/LearningStatusPage'
import { AiqLearnerReport } from '@/pages/AiqLearnerReport'
import { BookListPage } from '@/pages/books/BookListPage'
import { BookEditPage } from '@/pages/books/BookEditPage'
import { BookBulkCreatePage } from '@/pages/books/BookBulkCreatePage'
import { TextbookRequestsPage } from '@/pages/books/TextbookRequestsPage'
import { TextbookProgressBoardPage } from '@/pages/books/TextbookProgressBoardPage'
import { CurriculumBuilderPage } from '@/pages/CurriculumBuilderPage'
import { DataPromotionPage } from '@/pages/DataPromotionPage'
import { ContentTabLayout } from '@/pages/ContentTabLayout'
import { MathHeadWorldMappingPage } from '@/pages/contents/MathHeadWorldMappingPage'
import { LessonPreviewPage } from '@/pages/books/LessonPreviewPage'
import { AnswerKeyPreviewPage } from '@/pages/books/AnswerKeyPreviewPage'
import { FullBookPreviewPage } from '@/pages/books/FullBookPreviewPage'
import QuetureGradeManagement from '@/pages/QuetureGradeManagement'
import GameManagement from '@/pages/GameManagement'
import { ContentInspection } from '@/pages/ContentInspection'
import QuizSetManagement from '@/pages/QuizSetManagement'
import { RoleManagement } from '@/pages/RoleManagement'
import { LcContentFeeConfigPage } from '@/pages/LcContentFeeConfigPage'
import { LcSettlementPage } from '@/pages/lcSettlement/LcSettlementPage'
import { MyPage } from '@/pages/MyPage'
import { LcCrmLayout } from '@/pages/crm/LcCrmLayout'
import { CrmDashboardPage } from '@/pages/crm/CrmDashboardPage'
import { CrmInboxPage } from '@/pages/crm/CrmInboxPage'
import { CrmLeadsPage } from '@/pages/crm/CrmLeadsPage'
import { CrmLeadDetailPage } from '@/pages/crm/CrmLeadDetailPage'
import { CrmMergePage } from '@/pages/crm/CrmMergePage'
import { CrmImportPage } from '@/pages/crm/CrmImportPage'
import TocPreviewDev from '@/pages/dev/TocPreviewDev'
import ModuleSummaryPreviewDev from '@/pages/dev/ModuleSummaryPreviewDev'
import { useAuthStore } from '@/store/authStore'

const MallLayout = lazy(() =>
  import('@/pages/mall/MallLayout').then((module) => ({ default: module.MallLayout })),
)
const MallProductListPage = lazy(() =>
  import('@/pages/mall/MallProductListPage').then((module) => ({ default: module.MallProductListPage })),
)
const MallProductEditPage = lazy(() =>
  import('@/pages/mall/MallProductEditPage').then((module) => ({ default: module.MallProductEditPage })),
)
const MallOrderListPage = lazy(() =>
  import('@/pages/mall/MallOrderListPage').then((module) => ({ default: module.MallOrderListPage })),
)
const MallOrderDetailPage = lazy(() =>
  import('@/pages/mall/MallOrderDetailPage').then((module) => ({ default: module.MallOrderDetailPage })),
)
const MallReconciliationPage = lazy(() =>
  import('@/pages/mall/MallReconciliationPage').then((module) => ({ default: module.MallReconciliationPage })),
)

function MallRouteFallback() {
  return (
    <div className="-m-6 grid min-h-[calc(100vh-4rem)] place-items-center bg-[#f5f4ef] lg:-m-8">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-amber-500" />
        <p className="mt-3 text-sm font-medium text-slate-500">깨봉몰 운영 화면을 불러오는 중입니다.</p>
      </div>
    </div>
  )
}

function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { hasPermission } = useAuthStore()

  if (!hasPermission(permission)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function SuperRoute({ children }: { children: React.ReactNode }) {
  const { isSuper } = useAuthStore()

  if (!isSuper()) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading, hasCheckedAuth } = useAuthStore()

  // 인증 확인이 완료되지 않았거나 확인 중이면 로딩 표시
  if (!hasCheckedAuth || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-amber-500"></div>
          <p className="mt-4 text-slate-400">인증 확인 중...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.userType !== 'admin') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="p-8 max-w-md text-center bg-white rounded-xl">
          <h1 className="mb-4 text-2xl font-bold text-slate-900">접근 권한 없음</h1>
          <p className="mb-6 text-slate-600">관리자 권한이 필요합니다.</p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="btn-primary btn-md"
          >
            로그아웃
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// Public Only Route - 로그인 상태에서 접근 시 메인으로 리다이렉트
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, hasCheckedAuth } = useAuthStore()

  if (!hasCheckedAuth) return null

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  const { checkAuth, isHydrated, setHydrated } = useAuthStore()
  const hasInitialized = useRef(false)

  // 1. Rehydration 설정
  useEffect(() => {
    setHydrated()
  }, [setHydrated])

  // 2. 인증 초기화 - isHydrated가 true일 때 checkAuth 실행
  useEffect(() => {
    if (!isHydrated || hasInitialized.current) return
    hasInitialized.current = true
    checkAuth()
  }, [isHydrated, checkAuth])

  // 3. 로딩 상태 처리
  if (!isHydrated) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-amber-500"></div>
          <p className="mt-4 text-slate-400">시스템 초기화 중...</p>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/dev/toc" element={<ProtectedRoute><TocPreviewDev /></ProtectedRoute>} />
      <Route path="/dev/module-summary" element={<ProtectedRoute><ModuleSummaryPreviewDev /></ProtectedRoute>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<PermissionRoute permission="DASHBOARD"><AdminDashboard /></PermissionRoute>} />
        <Route path="users" element={<PermissionRoute permission="USERS"><UserManagement /></PermissionRoute>} />
        <Route path="organizations" element={<PermissionRoute permission="ORGANIZATIONS"><OrganizationManagement /></PermissionRoute>} />
        <Route path="organizations/:id" element={<PermissionRoute permission="ORGANIZATIONS"><OrganizationDetailPage /></PermissionRoute>} />
        <Route path="subscriptions/org" element={<PermissionRoute permission="SUBSCRIPTIONS_ORG"><OrgSubscriptionManagement /></PermissionRoute>} />
        <Route path="subscriptions/personal" element={<PermissionRoute permission="SUBSCRIPTIONS_PERSONAL"><SubscriptionManagement /></PermissionRoute>} />
        <Route path="subscriptions" element={<PermissionRoute permission="SUBSCRIPTIONS_ORG"><OrgSubscriptionManagement /></PermissionRoute>} />
        <Route path="products" element={<PermissionRoute permission="PRODUCTS"><ProductManagement /></PermissionRoute>} />
        <Route
          path="mall"
          element={(
            <PermissionRoute permission="MALL">
              <Suspense fallback={<MallRouteFallback />}>
                <MallLayout />
              </Suspense>
            </PermissionRoute>
          )}
        >
          <Route index element={<Navigate to="products" replace />} />
          <Route path="products" element={<MallProductListPage />} />
          <Route path="products/new" element={<MallProductEditPage />} />
          <Route path="products/:id" element={<MallProductEditPage />} />
          <Route path="orders" element={<MallOrderListPage />} />
          <Route path="orders/:id" element={<MallOrderDetailPage />} />
          <Route path="reconciliation" element={<MallReconciliationPage />} />
        </Route>
        <Route path="contents" element={<PermissionRoute permission="CONTENTS"><ContentTabLayout /></PermissionRoute>}>
          <Route index element={<ContentManagement />} />
          <Route path="curriculum-builder" element={<CurriculumBuilderPage />} />
          <Route path="curriculum-builder/:curriculumId" element={<CurriculumBuilderPage />} />
          <Route path="data-promotion" element={<SuperRoute><DataPromotionPage /></SuperRoute>} />
          <Route path="queture-contents" element={<QuetureGradeManagement />} />
          <Route path="books" element={<BookListPage />} />
          <Route path="books/bulk-new" element={<BookBulkCreatePage />} />
          <Route path="books/new" element={<BookEditPage />} />
          <Route path="books/:id" element={<BookEditPage />} />
          <Route path="books/:bookId/preview/:lessonId" element={<LessonPreviewPage />} />
          <Route path="books/:bookId/answer-key/:lessonId" element={<AnswerKeyPreviewPage />} />
          <Route path="books/:bookId/preview-all" element={<FullBookPreviewPage />} />
          <Route path="textbook-requests" element={<TextbookRequestsPage />} />
          <Route path="textbook-progress" element={<TextbookProgressBoardPage />} />
          <Route path="games" element={<GameManagement />} />
          <Route path="inspection" element={<ContentInspection />} />
          <Route path="quiz-sets" element={<QuizSetManagement />} />
          <Route path="math-head-worlds" element={<MathHeadWorldMappingPage />} />
        </Route>
        <Route path="configurations" element={<PermissionRoute permission="CONFIGURATIONS"><ConfigurationManagement /></PermissionRoute>} />
        <Route path="announcements" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementListPage /></PermissionRoute>} />
        <Route path="announcements/new" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementEditPage /></PermissionRoute>} />
        <Route path="announcements/:id" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementDetailPage /></PermissionRoute>} />
        <Route path="announcements/:id/edit" element={<PermissionRoute permission="ANNOUNCEMENTS"><AnnouncementEditPage /></PermissionRoute>} />
        <Route path="support-inquiries" element={<PermissionRoute permission="SUPPORT_INQUIRIES"><SupportInquiryPage /></PermissionRoute>} />
        <Route path="library" element={<PermissionRoute permission="LIBRARY"><SharedMediaLibraryPage /></PermissionRoute>} />
        <Route path="notes" element={<PermissionRoute permission="NOTES"><NotesPage /></PermissionRoute>} />
        <Route path="learning-status" element={<PermissionRoute permission="LEARNING_STATUS"><LearningStatusPage /></PermissionRoute>} />
        <Route path="aiq-learner-report" element={<PermissionRoute permission="LEARNING_STATUS"><AiqLearnerReport /></PermissionRoute>} />
        <Route path="data-stats" element={<PermissionRoute permission="DATA_STATS"><DataStatsPage /></PermissionRoute>} />
        <Route path="lc-crm" element={<PermissionRoute permission="LC_CRM"><LcCrmLayout /></PermissionRoute>}>
          <Route index element={<Navigate to="/lc-crm/dashboard" replace />} />
          <Route path="dashboard" element={<CrmDashboardPage />} />
          <Route path="inbox" element={<CrmInboxPage />} />
          <Route path="leads" element={<CrmLeadsPage />} />
          <Route path="leads/:leadId" element={<CrmLeadDetailPage />} />
          <Route path="merge" element={<CrmMergePage />} />
          <Route path="import" element={<CrmImportPage />} />
        </Route>
        <Route path="lc-settlement" element={<PermissionRoute permission="LC_SETTLEMENT"><LcSettlementPage /></PermissionRoute>} />
        <Route path="roles" element={<SuperRoute><RoleManagement /></SuperRoute>} />
        <Route path="lc-content-fee" element={<SuperRoute><LcContentFeeConfigPage /></SuperRoute>} />
        <Route path="my" element={<MyPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
